import React, { useEffect, useState, useCallback, useMemo } from "react";
import { format } from "date-fns";
import { TrendingUp } from "lucide-react";
import { Toaster, toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { api, formatINR, formatNum, formatPct } from "@/lib/api";
import { logError } from "@/lib/logger";
import { fyRange, fyStartYear } from "@/lib/dateFilter";
import { KpiTile } from "@/components/KpiTile";
import { Card } from "@/components/Card";
import { FilterBar } from "@/components/FilterBar";
import { SyncStatusIndicator } from "@/components/SyncStatusIndicator";
import {
  RevenueTrendChart,
  HBarChart,
  StackedBRCChart,
  DonutChart,
} from "@/components/Charts";
import { Leaderboard, RecentEnrolmentsTable, RecentPaymentsTable } from "@/components/Tables";
import { KpiDrilldown } from "@/components/KpiDrilldown";
import { DataHygienePanel } from "@/components/DataHygiene";

const buildInitialFilters = () => {
  const [s, e] = fyRange(fyStartYear(new Date()));
  return {
    start_date: format(s, "yyyy-MM-dd"),
    end_date: format(e, "yyyy-MM-dd"),
    quarter: null,
    year: null,
    vertical: [],
    program: [],
    salesperson: [],
    cohort: [],
    client_type: [],
  };
};

const cleanParams = (f) => {
  const out = {};
  for (const k in f) {
    const v = f[k];
    if (v === null || v === undefined || v === "") continue;
    if (Array.isArray(v)) {
      if (v.length === 0) continue;
      out[k] = v.join(",");
    } else {
      out[k] = v;
    }
  }
  return out;
};

export default function Dashboard() {
  const [filters, setFilters] = useState(buildInitialFilters);
  const [options, setOptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncMeta, setSyncMeta] = useState(null);

  const [kpis, setKpis] = useState(null);
  const [trend, setTrend] = useState([]);
  const [byVert, setByVert] = useState([]);
  const [byProg, setByProg] = useState([]);
  const [byCt, setByCt] = useState([]);
  const [byPType, setByPType] = useState([]);
  const [payStatus, setPayStatus] = useState([]);

  // GST mode toggle — "gross" (default, includes GST) or "net" (GST-exclusive, investor view)
  const [gstMode, setGstMode] = useState("gross");
  const isNet = gstMode === "net";

  // KPI drill-down — null when closed, otherwise one of:
  // bookings | revenue | collection | outstanding | students | salespeople
  const [drillKpi, setDrillKpi] = useState(null);

  const params = useMemo(
    () => ({ ...cleanParams(filters), mode: gstMode }),
    [filters, gstMode]
  );

  const loadFilters = useCallback(async () => {
    try {
      const o = await api.filters();
      setOptions(o);
    } catch (e) {
      logError(e);
    }
  }, []);

  const loadStatus = useCallback(async () => {
    try {
      const s = await api.syncStatus();
      setSyncMeta(s);
    } catch (e) {
      logError(e);
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [k, t, v, p, ct, pt, ps] = await Promise.all([
        api.kpis(params),
        api.revenueTrend(params),
        api.byVertical(params),
        api.byProgram(params),
        api.byClientType(params),
        api.byProgramType(params),
        api.paymentStatus(params),
      ]);
      setKpis(k);
      setTrend(t.series || []);
      setByVert(v.rows || []);
      setByProg((p.rows || []).slice(0, 8));
      setByCt(ct.rows || []);
      setByPType(pt.rows || []);
      setPayStatus(ps.rows || []);
    } catch (e) {
      logError(e);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    loadFilters();
    loadStatus();
  }, [loadFilters, loadStatus]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const onSync = async () => {
    setSyncing(true);
    toast.loading("Pulling fresh data from Airtable…", { id: "sync" });
    try {
      await api.triggerSync();
      toast.success("Synced from Airtable", { id: "sync" });
      await Promise.all([loadFilters(), loadStatus(), loadAll()]);
    } catch (e) {
      logError(e);
      toast.error("Sync failed", { id: "sync" });
    } finally {
      setSyncing(false);
    }
  };

  const lastSyncAt = syncMeta?.meta?.finished_at
    ? format(new Date(syncMeta.meta.finished_at), "d MMM yyyy, HH:mm")
    : "—";

  const totalRecords = syncMeta?.counts
    ? Object.values(syncMeta.counts).reduce((a, b) => a + b, 0)
    : 0;

  // Refreshing indicator: any active data fetch caused by filter change
  const isRefreshing = loading;

  return (
    <div className="grain min-h-screen relative">
      <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <header className="flex flex-wrap items-end justify-between gap-4 mb-6" data-testid="header">
          <div>
            <div className="mb-3 flex items-center gap-2" data-testid="brand-logo-row">
              <img
                src="/bower-logo.png"
                alt="Bower School of Entrepreneurship"
                className="h-12 sm:h-14 w-auto select-none"
                draggable={false}
                data-testid="brand-logo"
              />
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.02]">
              Sales <em className="not-italic" style={{ color: "#E76F1A" }}>MIS</em> Dashboard
            </h1>
            <p className="mt-2 text-sm text-muted-foreground max-w-xl">
              Real-time revenue, pipeline and conversion telemetry across verticals,
              programs and the sales floor — sourced live from Airtable.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap" data-testid="sync-cluster">
            <SyncStatusIndicator
              syncing={syncing}
              meta={syncMeta?.meta}
              autoSync={syncMeta?.auto_sync}
              totalRecords={totalRecords}
              onSync={onSync}
            />
            <div
              className="inline-flex items-center rounded-full border border-border bg-background/70 p-0.5 text-[11px]"
              data-testid="gst-toggle"
              role="group"
              aria-label="GST mode"
            >
              <button
                type="button"
                onClick={() => setGstMode("gross")}
                className={`px-3 h-8 rounded-full font-medium transition-colors ${
                  !isNet
                    ? "bg-[#1F3A5F] text-white shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                aria-pressed={!isNet}
                title="Show data inclusive of GST"
                data-testid="gst-toggle-gross"
              >
                With GST
              </button>
              <button
                type="button"
                onClick={() => setGstMode("net")}
                className={`px-3 h-8 rounded-full font-medium transition-colors ${
                  isNet
                    ? "bg-[#2BA88A] text-white shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                aria-pressed={isNet}
                title="Show Net (GST-exclusive) data — investor view"
                data-testid="gst-toggle-net"
              >
                Net (ex-GST)
              </button>
            </div>
          </div>
        </header>

        {/* Filters - sticky floating */}
        <div className="sticky top-0 z-40 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 mb-6 bg-[hsl(var(--bg))]/80 backdrop-blur-xl border-b border-border/60">
          <FilterBar
            filters={filters}
            options={options}
            onChange={setFilters}
            onReset={() => setFilters(buildInitialFilters())}
          />
          {/* Top progress bar shown while data is refreshing after any filter / search / pagination change */}
          <div
            className={`absolute left-0 right-0 -bottom-[1px] h-[2px] overflow-hidden pointer-events-none transition-opacity duration-200 ${
              isRefreshing ? "opacity-100" : "opacity-0"
            }`}
            data-testid="refresh-progress"
            aria-hidden={!isRefreshing}
          >
            <div className="h-full w-1/3 bg-gradient-to-r from-transparent via-[#E76F1A] to-transparent animate-progress-slide" />
          </div>
        </div>

        {/* KPI grid */}
        <section
          className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 mb-6 transition-opacity duration-200 ${
            loading && kpis ? "opacity-60" : "opacity-100"
          }`}
          data-testid="kpi-grid"
          aria-busy={loading}
        >
          {loading && !kpis ? (
            Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={`kpi-skeleton-${i}`} className="h-[120px] rounded-2xl" />
            ))
          ) : (
            <>
              <KpiTile
                label={isNet ? "Net Bookings" : "Total Bookings (TOV)"}
                value={kpis?.total_bookings}
                format="inr"
                glyph="₹"
                variant="slate"
                hint={`Students ${formatINR(kpis?.student_bookings)} · Partnerships ${formatINR(kpis?.partnership_bookings)}`}
                testId="kpi-total-bookings"
                onClick={() => setDrillKpi("bookings")}
              />
              <KpiTile
                label={isNet ? "Net Revenue (Recognised)" : "Revenue (Recognized)"}
                value={kpis?.recognized_revenue}
                format="inr"
                glyph="₹"
                variant="revenue"
                hint={`Students ${formatINR(kpis?.student_recognized_revenue)} · Partnerships ${formatINR(kpis?.partnership_recognized_revenue)}`}
                testId="kpi-recognized-revenue"
                onClick={() => setDrillKpi("revenue")}
              />
              <KpiTile
                label={isNet ? "Net Collection" : "Total Collection"}
                value={kpis?.total_collection}
                format="inr"
                glyph="✓"
                variant="collection"
                hint={`${formatPct(kpis?.collection_rate)} of ${isNet ? "net " : ""}bookings · from Payments`}
                testId="kpi-total-collection"
                onClick={() => setDrillKpi("collection")}
              />
              <KpiTile
                label={isNet ? "Net Outstanding" : "Outstanding (Pipeline)"}
                value={kpis?.pipeline_value}
                format="inr"
                glyph="∑"
                variant="danger"
                hint={
                  kpis?.dropped_count > 0
                    ? `Excl. ${formatNum(kpis.dropped_count)} dropped · ${
                        kpis?.advance > 0 ? `Advance ${formatINR(kpis.advance)}` : "Active receivables"
                      }`
                    : kpis?.advance > 0
                    ? `Advance ${formatINR(kpis.advance)} against future revenue`
                    : isNet ? "Net Revenue − Net Collection" : "Revenue − Collection"
                }
                testId="kpi-pipeline"
                onClick={() => setDrillKpi("outstanding")}
              />
              <KpiTile
                label="Lost Bookings"
                value={kpis?.lost_bookings}
                format="inr"
                glyph="✗"
                hint={
                  kpis?.refund_eligible > 0
                    ? `+ ₹${formatINR(kpis.refund_eligible)} refund-eligible across ${formatNum(kpis.dropped_count)} dropped`
                    : "TOV booked but never collected (drop-out write-off)"
                }
                testId="kpi-lost-bookings"
                onClick={() => setDrillKpi("dropouts")}
              />
              <KpiTile
                label="Collection Rate"
                value={kpis?.collection_rate}
                format="pct"
                glyph="%"
                hint={isNet ? "Net Collected ÷ Net Bookings" : "Collected ÷ Bookings"}
                testId="kpi-collection-rate"
                onClick={() => setDrillKpi("collection")}
              />
              <KpiTile
                label="Conversion Rate"
                value={kpis?.conversion_rate}
                format="pct"
                glyph="↑"
                hint="Students who paid"
                testId="kpi-conversion-rate"
              />
              <KpiTile
                label="Total Students"
                value={kpis?.total_students}
                format="num"
                glyph="∎"
                hint={`${formatNum(kpis?.active_students)} active · ${formatNum(kpis?.paying_students)} paying`}
                testId="kpi-total-students"
                onClick={() => setDrillKpi("students")}
              />
              <KpiTile
                label="Avg Deal Size"
                value={kpis?.avg_revenue_per_student}
                format="inr"
                glyph="◇"
                hint="Per student"
                testId="kpi-avg-deal"
              />
              <KpiTile
                label="Active Sales People"
                value={kpis?.active_sales_people}
                format="num"
                glyph="◐"
                hint={`${formatNum(kpis?.total_sales_people)} total`}
                testId="kpi-active-sales"
                onClick={() => setDrillKpi("salespeople")}
              />
              <KpiTile
                label="Total Partnerships"
                value={kpis?.total_partnerships}
                format="num"
                glyph="◑"
                hint={`${formatNum(kpis?.active_partnerships)} active`}
                testId="kpi-total-partnerships"
                onClick={() => setDrillKpi("partnerships")}
              />
            </>
          )}
        </section>

        {/* Full-screen slide-over drill-down (Sheet) */}
        <KpiDrilldown
          kpi={drillKpi}
          isNet={isNet}
          params={params}
          onClose={() => setDrillKpi(null)}
        />

        {/* Charts row 1: trend + client type + program type */}
        <section
          className={`grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5 mb-6 transition-opacity duration-200 ${
            loading && kpis ? "opacity-60" : "opacity-100"
          }`}
          aria-busy={loading}
        >
          <Card
            title="Revenue & Collection Trend"
            subtitle={
              isNet
                ? "Monthly Net Bookings, Net Recognised Revenue & Net Collection (GST-exclusive)"
                : "Monthly Bookings (TOV), Recognised Revenue & realised Collection"
            }
            className="lg:col-span-2"
            testId="chart-trend"
            action={
              <span className="chip">
                <TrendingUp className="w-3 h-3" />
                {trend.length} mo
              </span>
            }
          >
            {trend.length === 0 ? (
              <div className="h-[290px] flex items-center justify-center text-sm text-muted-foreground">
                No data
              </div>
            ) : (
              <RevenueTrendChart data={trend} />
            )}
          </Card>
          <Card title="Client Mix" subtitle="Revenue split by client type" testId="chart-client-mix">
            {byCt.length === 0 ? (
              <div className="h-[280px] flex items-center justify-center text-sm text-muted-foreground">
                No data
              </div>
            ) : (
              <DonutChart data={byCt} />
            )}
          </Card>
        </section>

        {/* Charts row 2: vertical + program */}
        <section
          className={`grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5 mb-6 transition-opacity duration-200 ${
            loading && kpis ? "opacity-60" : "opacity-100"
          }`}
          aria-busy={loading}
        >
          <Card title="Booking, Revenue & Collection by Vertical" subtitle="Performance across business verticals" testId="chart-vertical">
            {byVert.length === 0 ? (
              <div className="h-[320px] flex items-center justify-center text-sm text-muted-foreground">
                No data
              </div>
            ) : (
              <StackedBRCChart data={byVert} />
            )}
          </Card>
          <Card title="Top Programs" subtitle="Top 8 programs by revenue contribution" testId="chart-program">
            {byProg.length === 0 ? (
              <div className="h-[320px] flex items-center justify-center text-sm text-muted-foreground">
                No data
              </div>
            ) : (
              <HBarChart data={byProg} />
            )}
          </Card>
        </section>

        {/* Charts row 3: program type + payment status */}
        <section
          className={`grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5 mb-6 transition-opacity duration-200 ${
            loading && kpis ? "opacity-60" : "opacity-100"
          }`}
          aria-busy={loading}
        >
          <Card title="Program Type Mix" subtitle="High / Mid / Low ticket and GTM split" testId="chart-program-type">
            {byPType.length === 0 ? (
              <div className="h-[280px] flex items-center justify-center text-sm text-muted-foreground">
                No data
              </div>
            ) : (
              <DonutChart data={byPType} />
            )}
          </Card>
          <Card title="Installment Health" subtitle="Status of every installment booked" testId="chart-payments">
            {payStatus.length === 0 ? (
              <div className="h-[280px] flex items-center justify-center text-sm text-muted-foreground">
                No data
              </div>
            ) : (
              <DonutChart data={payStatus} valueKey="amount" formatter={formatINR} />
            )}
          </Card>
        </section>

        {/* Leaderboard / Recent Enrolments / Recent Payments — now accessed via KPI drill-downs */}

        {/* Data hygiene panel — catches Airtable data-entry mistakes */}
        <section className="mb-6">
          <DataHygienePanel onOrphanClick={() => setDrillKpi("collection-orphan")} />
        </section>

        <footer className="text-center text-xs text-muted-foreground py-6">
          Sales MIS · Built on Airtable Base{" "}
          <span className="tabular">appLHmbluj24cFkvR</span>
        </footer>
      </div>
      <Toaster richColors position="top-right" />
    </div>
  );
}
